import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual'
import { GripVerticalIcon } from 'lucide-react'

import { ScrollArea } from '~/client/src/shared/components/shadcn-ui/scroll-area'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import HierarchyOptionRow from './HierarchyOptionRow'
import {
  HierarchyNameDraftValidation,
  HierarchyOption,
} from './HierarchyPropertyConfig.types'
import { flattenHierarchyRows } from './hierarchyPropertyOptions'

type HierarchyRows = ReturnType<typeof flattenHierarchyRows>
type DragListeners = ReturnType<typeof useSortable>['listeners']

interface IHierarchyOptionsListProps {
  rows: HierarchyRows
  selectedValues: Set<string>
  expandedValues: Set<string>
  onSelectedChange: (value: string, checked: boolean) => void
  onExpandedToggle: (value: string) => void
  onUpdateOption: (
    index: number,
    changes: Partial<Pick<HierarchyOption, 'label' | 'value'>>,
  ) => void
  onNameManuallyEdited: (value: string) => void
  onLabelBlur: (index: number) => void
  getNameDraftValidation: (
    index: number,
    draft: string,
  ) => HierarchyNameDraftValidation
  onRestoreAndApply: (index: number, deletedOption: HierarchyOption) => void
  onReorder: (oldIndex: number, newIndex: number) => void
  onInsertBelow?: (value: string) => void
  autoFocusValue?: string | null
  onAutoFocusHandled?: () => void
}

// Rows are uniform single-line controls; the virtualizer measures the real
// height per row, so this is only the pre-measurement estimate.
const ESTIMATED_ROW_HEIGHT = 44
const OVERSCAN = 10
// Drag must travel a few px before it starts so a click on the handle (or the
// label input next to it) still registers as a click, not a drag.
const DRAG_ACTIVATION_DISTANCE_PX = 5

// The list is windowed, so only the overscanned rows are mounted; the default
// sorting strategies shift sibling positions by reading every item's geometry,
// which is wrong with off-screen rows. A no-op strategy disables sibling shift
// entirely — drag feedback comes from the DragOverlay and reordering is done by
// value lookup in onDragEnd, neither of which needs the strategy.
const noopSortingStrategy = () => null

const DragHandle: React.FC<{
  label: string
  attributes: DraggableAttributes
  listeners?: DragListeners
}> = ({ label, attributes, listeners }) => (
  <div
    {...attributes}
    {...listeners}
    aria-label={Localization.translator.reorderOption(label)}
    className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:cursor-grab tw:text-muted-foreground tw:active:cursor-grabbing"
  >
    <GripVerticalIcon className="tw:size-4" />
  </div>
)

interface ISortableOptionRowProps {
  row: HierarchyRows[number]
  virtualItem: VirtualItem
  measureElement: (node: Element | null) => void
  isSelected: boolean
  isExpanded: boolean
  onSelectedChange: IHierarchyOptionsListProps['onSelectedChange']
  onExpandedToggle: IHierarchyOptionsListProps['onExpandedToggle']
  onUpdateOption: IHierarchyOptionsListProps['onUpdateOption']
  onNameManuallyEdited: IHierarchyOptionsListProps['onNameManuallyEdited']
  onLabelBlur: IHierarchyOptionsListProps['onLabelBlur']
  getNameDraftValidation: IHierarchyOptionsListProps['getNameDraftValidation']
  onRestoreAndApply: IHierarchyOptionsListProps['onRestoreAndApply']
  onInsertBelow: IHierarchyOptionsListProps['onInsertBelow']
  autoFocusValue: IHierarchyOptionsListProps['autoFocusValue']
  onAutoFocusHandled: IHierarchyOptionsListProps['onAutoFocusHandled']
}

const SortableOptionRow: React.FC<ISortableOptionRowProps> = ({
  row,
  virtualItem,
  measureElement,
  isSelected,
  isExpanded,
  onSelectedChange,
  onExpandedToggle,
  onUpdateOption,
  onNameManuallyEdited,
  onLabelBlur,
  getNameDraftValidation,
  onRestoreAndApply,
  onInsertBelow,
  autoFocusValue,
  onAutoFocusHandled,
}) => {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: row.option.value,
  })

  // Rows sit in normal flow inside the virtualizer's single translated window
  // wrapper (matching CardList). The virtualizer measures each row's real
  // height and dnd-kit needs the same node for drag detection, so merge their
  // refs once — a stable callback avoids re-running the ResizeObserver each
  // render.
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node)
      measureElement(node)
    },
    [setNodeRef, measureElement],
  )

  return (
    <div
      ref={setRef}
      data-index={virtualItem.index}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <HierarchyOptionRow
        row={row}
        isSelected={isSelected}
        isExpanded={isExpanded}
        dragHandle={
          <DragHandle
            label={row.option.label}
            attributes={attributes}
            listeners={listeners}
          />
        }
        onSelectedChange={onSelectedChange}
        onExpandedToggle={onExpandedToggle}
        onUpdateOption={onUpdateOption}
        onNameManuallyEdited={onNameManuallyEdited}
        onLabelBlur={onLabelBlur}
        getNameDraftValidation={getNameDraftValidation}
        onRestoreAndApply={onRestoreAndApply}
        onInsertBelow={onInsertBelow}
        autoFocusValue={autoFocusValue}
        onAutoFocusHandled={onAutoFocusHandled}
      />
    </div>
  )
}

const HierarchyOptionsList: React.FC<IHierarchyOptionsListProps> = ({
  rows,
  selectedValues,
  expandedValues,
  onSelectedChange,
  onExpandedToggle,
  onUpdateOption,
  onNameManuallyEdited,
  onLabelBlur,
  getNameDraftValidation,
  onRestoreAndApply,
  onReorder,
  onInsertBelow,
  autoFocusValue,
  onAutoFocusHandled,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeValue, setActiveValue] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // The ScrollArea wrapper is a non-ref-forwarding primitive, so reach its
  // base-ui viewport (the real scroll container) by data-slot for the
  // virtualizer instead of the outer wrapper, which never scrolls.
  const getScrollElement = useCallback(
    () =>
      scrollRef.current?.querySelector<HTMLDivElement>(
        '[data-slot="scroll-area-viewport"]',
      ) ?? null,
    [],
  )

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: index => rows[index]?.option.value ?? `__row:${index}`,
  })

  // A freshly created option may sit outside the windowed range, so scroll it
  // into view; mounting its row lets the row focus its own input.
  useEffect(() => {
    if (!autoFocusValue) return
    const index = rows.findIndex(row => row.option.value === autoFocusValue)
    if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' })
  }, [autoFocusValue, rows, virtualizer])

  const sortableIds = useMemo(() => rows.map(row => row.option.value), [rows])

  // Sortable ids are internal normalized option values; resolve them back to
  // visible labels so screen-reader drag announcements say "Building A", not
  // "building_a_2".
  const announcements: Announcements = useMemo(() => {
    const labelFor = (id: string | number) =>
      rows.find(row => row.option.value === id)?.option.label ?? String(id)
    return {
      onDragStart: ({ active }) =>
        Localization.translator.pickedUpOption(labelFor(active.id)),
      onDragOver: ({ active, over }) =>
        over
          ? Localization.translator.optionIsOver(
              labelFor(active.id),
              labelFor(over.id),
            )
          : undefined,
      onDragEnd: ({ active, over }) =>
        over
          ? Localization.translator.optionDroppedOver(
              labelFor(active.id),
              labelFor(over.id),
            )
          : Localization.translator.optionDropped(labelFor(active.id)),
      onDragCancel: ({ active }) =>
        Localization.translator.reorderingOptionCancelled(labelFor(active.id)),
    }
  }, [rows])

  const activeRow = activeValue
    ? rows.find(row => row.option.value === activeValue)
    : undefined

  const handleDragStart = (event: DragStartEvent) => {
    setActiveValue(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveValue(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = rows.findIndex(row => row.option.value === active.id)
    const newIndex = rows.findIndex(row => row.option.value === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(oldIndex, newIndex)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      accessibility={{ announcements }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveValue(null)}
    >
      <SortableContext items={sortableIds} strategy={noopSortingStrategy}>
        <div ref={scrollRef}>
          {/* The viewport is the element with overflow:scroll, so the height
              cap must land there — capping the Root instead leaves the
              size-full viewport unbounded (its % height can't resolve against a
              max-height-only parent) and the list grows tall without scrolling. */}
          <ScrollArea className="tw:[&>[data-slot=scroll-area-viewport]]:max-h-[60vh]">
            <div
              className="tw:relative tw:w-full"
              style={{ height: virtualizer.getTotalSize() }}
            >
              <div
                className="tw:absolute tw:left-0 tw:top-0 tw:w-full tw:px-4"
                style={{
                  transform: `translateY(${
                    virtualizer.getVirtualItems()[0]?.start ?? 0
                  }px)`,
                }}
              >
                {virtualizer.getVirtualItems().map(virtualItem => {
                  const row = rows[virtualItem.index]
                  if (!row) return null
                  return (
                    <SortableOptionRow
                      key={String(virtualItem.key)}
                      row={row}
                      virtualItem={virtualItem}
                      measureElement={virtualizer.measureElement}
                      isSelected={selectedValues.has(row.option.value)}
                      isExpanded={expandedValues.has(row.option.value)}
                      onSelectedChange={onSelectedChange}
                      onExpandedToggle={onExpandedToggle}
                      onUpdateOption={onUpdateOption}
                      onNameManuallyEdited={onNameManuallyEdited}
                      onLabelBlur={onLabelBlur}
                      getNameDraftValidation={getNameDraftValidation}
                      onRestoreAndApply={onRestoreAndApply}
                      onInsertBelow={onInsertBelow}
                      autoFocusValue={autoFocusValue}
                      onAutoFocusHandled={onAutoFocusHandled}
                    />
                  )
                })}
              </div>
            </div>
          </ScrollArea>
        </div>
      </SortableContext>
      <DragOverlay>
        {activeRow ? (
          // Inert visual-only preview: the dragged row's real controls
          // (checkbox, expand button, label input, popover) must not be cloned
          // into the AT/focus tree during a drag, so render a non-interactive
          // grip + label instead of a live HierarchyOptionRow.
          <div
            aria-hidden="true"
            className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-background tw:px-2 tw:py-1.5 tw:shadow-lg"
          >
            <div className="tw:flex tw:size-6 tw:items-center tw:justify-center tw:text-muted-foreground">
              <GripVerticalIcon className="tw:size-4" />
            </div>
            <span className="tw:truncate tw:text-body-md">
              {activeRow.option.label}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default HierarchyOptionsList
